import { useState } from "react";
import { authAPI } from "@/lib/api";
import { useTranslation } from "@/lib/i18n";
import { LanguageSelector } from "@/components/shared/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Settings, Lock, Globe, Bell, Trash2, ChevronLeft, Eye, EyeOff, CheckCircle2, AlertTriangle, Crown, Download, Shield
} from "lucide-react";
import { toast } from "sonner";
import PrivacyCenter from "@/components/PrivacyCenter";

export function SettingsView({ user, onBack, onLogout, onDeleteAccount, onPremium }) {
  const { t } = useTranslation();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [notifications, setNotifications] = useState({
    messages: true,
    profile_views: true,
    promotions: true,
  });
  const [exporting, setExporting] = useState(false);

  const isOAuth = !user?.password_hash && user?.picture;

  const handleChangePassword = async () => {
    if (!currentPw || !newPw) { toast.error(t("settings.pw_required")); return; }
    if (newPw.length < 6) { toast.error(t("settings.pw_min_length")); return; }
    if (newPw !== confirmPw) { toast.error(t("settings.pw_mismatch")); return; }
    setChangingPw(true);
    try {
      await authAPI.changePassword({ current_password: currentPw, new_password: newPw });
      toast.success(t("settings.pw_changed"));
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      const msg = err.response?.data?.detail || "Error";
      toast.error(msg);
    } finally { setChangingPw(false); }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await authAPI.deleteAccount();
      toast.success(t("account.deleted"));
      localStorage.removeItem("session_token");
      window.location.href = "/";
    } catch { toast.error("Error"); } finally { setDeleting(false); }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const res = await authAPI.exportData();
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `staffera-data-${user?.email || "export"}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("settings.export_success"));
    } catch { toast.error("Error"); } finally { setExporting(false); }
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="settings-view">
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-gold-600 font-body mb-4 transition-colors" data-testid="settings-back-btn">
          <ChevronLeft className="w-4 h-4" /> {t("cd.back_overview")}
        </button>
      )}

      <div className="flex items-center gap-3 mb-6">
        <Settings className="w-6 h-6 text-slate-700" />
        <h2 className="font-sans text-xl font-bold text-slate-900">{t("settings.title")}</h2>
      </div>

      {/* Change Password */}
      <Card className="border-slate-200 bg-white shadow-sm mb-4">
        <CardContent className="pt-5 pb-5 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <Lock className="w-4 h-4 text-slate-500" />
            <Label className="font-sans font-semibold text-sm text-slate-900">{t("settings.pw_title")}</Label>
          </div>
          {isOAuth ? (
            <p className="text-sm text-slate-400 font-body">{t("settings.pw_oauth")}</p>
          ) : (
            <>
              <div>
                <Label className="font-body text-xs text-slate-500 mb-1 block">{t("settings.pw_current")}</Label>
                <div className="relative">
                  <Input type={showCurrent ? "text" : "password"} value={currentPw} onChange={e => setCurrentPw(e.target.value)}
                    className="h-10 font-body pr-10" data-testid="settings-current-pw" />
                  <button type="button" className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600" onClick={() => setShowCurrent(!showCurrent)}>
                    {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="font-body text-xs text-slate-500 mb-1 block">{t("settings.pw_new")}</Label>
                <div className="relative">
                  <Input type={showNew ? "text" : "password"} value={newPw} onChange={e => setNewPw(e.target.value)}
                    className="h-10 font-body pr-10" data-testid="settings-new-pw" />
                  <button type="button" className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600" onClick={() => setShowNew(!showNew)}>
                    {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="font-body text-xs text-slate-500 mb-1 block">{t("settings.pw_confirm")}</Label>
                <Input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
                  className="h-10 font-body" data-testid="settings-confirm-pw" />
                {newPw && confirmPw && newPw !== confirmPw && (
                  <p className="text-xs text-red-500 font-body mt-1">{t("settings.pw_mismatch")}</p>
                )}
              </div>
              <Button className="bg-navy-900 hover:bg-navy-800 text-white font-semibold w-full h-10"
                onClick={handleChangePassword} disabled={changingPw || !currentPw || !newPw || newPw !== confirmPw}
                data-testid="settings-change-pw-btn">
                {changingPw ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                {t("settings.pw_btn")}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card className="border-slate-200 bg-white shadow-sm mb-4">
        <CardContent className="pt-5 pb-5 space-y-1">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-4 h-4 text-slate-500" />
            <Label className="font-sans font-semibold text-sm text-slate-900">{t("settings.notif_title")}</Label>
          </div>
          <div className="flex items-center justify-between py-2.5">
            <span className="text-sm text-slate-700 font-body">{t("settings.notif_messages")}</span>
            <Switch checked={notifications.messages} onCheckedChange={v => setNotifications(p => ({ ...p, messages: v }))} data-testid="settings-notif-messages" />
          </div>
          <Separator />
          <div className="flex items-center justify-between py-2.5">
            <span className="text-sm text-slate-700 font-body">{t("settings.notif_views")}</span>
            <Switch checked={notifications.profile_views} onCheckedChange={v => setNotifications(p => ({ ...p, profile_views: v }))} data-testid="settings-notif-views" />
          </div>
          <Separator />
          <div className="flex items-center justify-between py-2.5">
            <span className="text-sm text-slate-700 font-body">{t("settings.notif_promos")}</span>
            <Switch checked={notifications.promotions} onCheckedChange={v => setNotifications(p => ({ ...p, promotions: v }))} data-testid="settings-notif-promos" />
          </div>
        </CardContent>
      </Card>

      {/* Language */}
      <Card className="border-slate-200 bg-white shadow-sm mb-4">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-4 h-4 text-slate-500" />
            <Label className="font-sans font-semibold text-sm text-slate-900">{t("settings.language_title")}</Label>
          </div>
          <LanguageSelector variant="light" />
        </CardContent>
      </Card>

      {/* Account info */}
      <Card className="border-slate-200 bg-white shadow-sm mb-4">
        <CardContent className="pt-5 pb-5 space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-slate-500" />
            <Label className="font-sans font-semibold text-sm text-slate-900">{t("settings.account_title")}</Label>
          </div>
          <div className="text-sm font-body text-slate-600">
            <p><span className="text-slate-400">{t("auth.email")}:</span> {user?.email}</p>
            <p className="mt-1"><span className="text-slate-400">{t("settings.account_type")}:</span> {user?.role === "hotel" ? "Hotel" : t("settings.candidate")}</p>
            <p className="mt-1"><span className="text-slate-400">{t("cd.member_since", { date: "" })}:</span> {user?.created_at?.split("T")[0]}</p>
          </div>
        </CardContent>
      </Card>

      {/* GDPR - Data portability */}
      <Card className="border-slate-200 bg-white shadow-sm mb-4">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center gap-2 mb-3">
            <Download className="w-4 h-4 text-slate-500" />
            <Label className="font-sans font-semibold text-sm text-slate-900">{t("settings.gdpr_title")}</Label>
          </div>
          <p className="text-xs text-slate-500 font-body mb-3">{t("settings.gdpr_desc")}</p>
          <Button variant="outline" className="w-full" onClick={handleExportData} disabled={exporting} data-testid="settings-export-btn">
            {exporting ? <div className="w-4 h-4 border-2 border-slate-500 border-t-transparent rounded-full animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            {t("settings.export_btn")}
          </Button>
        </CardContent>
      </Card>

      {/* Privacy Center */}
      <Card className="border-slate-200 bg-white shadow-sm mb-4">
        <CardContent className="pt-5 pb-5">
          <PrivacyCenter user={user} />
        </CardContent>
      </Card>

      {/* Danger zone */}
      <Card className="border-red-200 bg-red-50/50 shadow-sm">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <Label className="font-sans font-semibold text-sm text-red-700">{t("settings.danger_title")}</Label>
          </div>
          <p className="text-xs text-red-600/70 font-body mb-3">{t("settings.danger_desc")}</p>
          <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-100 w-full"
            onClick={() => setShowDeleteDialog(true)} data-testid="settings-delete-btn">
            <Trash2 className="w-4 h-4 mr-2" /> {t("account.delete_title")}
          </Button>
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={v => { setShowDeleteDialog(v); if (!v) setDeleteConfirm(""); }}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0" data-testid="settings-delete-dialog">
          <DialogTitle className="sr-only">{t("account.delete_title")}</DialogTitle>
          <div className="bg-red-600 px-6 pt-6 pb-4 text-center">
            <AlertTriangle className="w-10 h-10 text-white mx-auto mb-2" />
            <h2 className="font-sans text-lg font-bold text-white">{t("account.delete_title")}</h2>
            <p className="text-sm text-red-100 font-body mt-1">{t("account.delete_warning")}</p>
          </div>
          <div className="px-6 py-5 space-y-3">
            <p className="text-sm text-slate-600 font-body">{t("account.delete_confirm_text", { keyword: t("account.delete_keyword") })}</p>
            <Input value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)}
              placeholder={t("account.delete_keyword")} className="font-body" data-testid="settings-delete-input" />
            <Button className="bg-red-600 hover:bg-red-700 text-white w-full"
              disabled={deleteConfirm !== t("account.delete_keyword") || deleting}
              onClick={handleDeleteAccount} data-testid="settings-delete-confirm-btn">
              {deleting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {t("account.delete_btn")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
